import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import SubscriptionBadge from "./SubscriptionBadge";
import api from "../services/api";

export default function AccountPage() {
  const { user, isAuthenticated, refreshUserData } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleManageSubscription = async () => {
    if (!user?.stripe_customer_id || !user?.customer_subscription_id) {
      setError("No active subscription found");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post("/manage-subscription");
      // Redirect to the Stripe portal
      window.location.href = response.data.url;
    } catch (err) {
      console.error("Error opening subscription portal:", err);
      setError("Failed to open subscription management portal");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center p-8 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">Not Authenticated</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Please log in to view your account details
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">
        Account Settings
      </h1>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">
          Profile Information
        </h2>
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {user?.picture && (
            <div className="flex-shrink-0">
              <img
                src={user.picture}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
              />
            </div>
          )}
          <div className="flex-grow space-y-3">
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Name
              </h3>
              <p className="text-gray-900 dark:text-white">
                {user?.name || "Not provided"}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Email
              </h3>
              <p className="text-gray-900 dark:text-white">{user?.email}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Member Since
              </h3>
              <p className="text-gray-900 dark:text-white">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : "Unknown"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold mb-4 dark:text-white">
              Subscription
            </h2>
            <div className="mb-4">
              <SubscriptionBadge />
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            <button
              onClick={handleManageSubscription}
              disabled={isLoading || !user?.customer_subscription_id}
              className={`px-4 py-2 rounded-md font-medium text-white 
                ${
                  user?.customer_subscription_id
                    ? "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
                    : "bg-gray-400 cursor-not-allowed"
                } 
                focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200`}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Manage Subscription"
              )}
            </button>
            {!user?.customer_subscription_id && (
              <div className="mt-2 text-sm text-gray-500">
                <a href="/pricing" className="text-blue-600 hover:underline">
                  Subscribe to a plan
                </a>{" "}
                to manage your subscription
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-md">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-white">Usage</h2>
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Available Video Credits
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {user?.videos_left || 0}
          </p>
        </div>
        <a
          href="mailto:mikay@ugz.ai"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          Need more credits? Contact us
          <svg
            className="ml-1 w-4 h-4"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
              clipRule="evenodd"
            ></path>
          </svg>
        </a>
      </div>
    </div>
  );
}
